/**
 * Saneado de nombres de archivo.
 *
 * Los nombres provenientes del cliente NUNCA se usan tal cual para construir
 * claves de R2 ni cabeceras HTTP. El nombre original se conserva en
 * `files.original_name` (sólo como dato), y la clave de R2 usa la versión
 * saneada de este módulo.
 */

const MAX_FILENAME_LENGTH = 200;
/** Categoría Unicode "Control" (C0 y C1): equivale a U+0000–U+001F y U+007F–U+009F. */
const CONTROL_CHARS = /\p{Cc}/gu;
/** Todo lo que no sea letra, número, punto, guion, guion bajo o espacio. */
const UNSAFE_CHARS = /[^\p{L}\p{N}._\- ]/gu;
/** Caracteres fuera del rango ASCII imprimible (para cabeceras HTTP). */
const NON_ASCII_PRINTABLE = /[^\p{ASCII}]|[\p{Cc}]/gu;

const RESERVED_WINDOWS_NAMES = new Set([
  'con',
  'prn',
  'aux',
  'nul',
  ...Array.from({ length: 9 }, (_, i) => `com${i + 1}`),
  ...Array.from({ length: 9 }, (_, i) => `lpt${i + 1}`),
]);

export const FALLBACK_FILENAME = 'file';

/** Devuelve el último segmento, descartando cualquier ruta enviada por el cliente. */
export function basename(input: string): string {
  const parts = input.split(/[\\/]/);
  return parts[parts.length - 1] ?? '';
}

/**
 * Convierte un nombre arbitrario en un nombre seguro para almacenamiento.
 *
 * - Descarta rutas (`../../etc/passwd` -> `passwd`).
 * - Elimina caracteres de control y separadores.
 * - Evita nombres reservados de Windows y nombres vacíos.
 * - Trunca a 200 caracteres preservando la extensión.
 */
export function sanitizeFilename(input: string): string {
  if (typeof input !== 'string') return FALLBACK_FILENAME;

  let name = basename(input.normalize('NFC'))
    .replace(CONTROL_CHARS, '')
    .replace(UNSAFE_CHARS, '_')
    .replace(/\s+/g, ' ')
    .replace(/_{2,}/g, '_')
    .trim();

  // `.` y `..` no son nombres válidos; los puntos iniciales ocultan el archivo.
  name = name.replace(/^[.\s]+/, '').replace(/[.\s]+$/, '');

  if (name.length === 0) return FALLBACK_FILENAME;

  const ext = extractExtension(name);
  const stem = ext ? name.slice(0, name.length - ext.length - 1) : name;

  if (RESERVED_WINDOWS_NAMES.has(stem.toLowerCase())) {
    return ext ? `${stem}_file.${ext}` : `${stem}_file`;
  }

  if (name.length > MAX_FILENAME_LENGTH) {
    const keep = ext ? MAX_FILENAME_LENGTH - ext.length - 1 : MAX_FILENAME_LENGTH;
    const truncatedStem = stem.slice(0, Math.max(1, keep));
    name = ext ? `${truncatedStem}.${ext}` : truncatedStem;
  }

  return name.length > 0 ? name : FALLBACK_FILENAME;
}

/** Extensión en minúsculas sin punto, o `null` si no tiene una válida. */
export function extractExtension(filename: string): string | null {
  const name = basename(filename);
  const idx = name.lastIndexOf('.');
  if (idx <= 0 || idx === name.length - 1) return null;
  const ext = name.slice(idx + 1).toLowerCase();
  return /^[a-z0-9]{1,12}$/.test(ext) ? ext : null;
}

/** Nombre de carpeta seguro: sin separadores, sin `.`/`..`, longitud acotada. */
export function sanitizeFolderName(input: string): string {
  const name = sanitizeFilename(input);
  return name === FALLBACK_FILENAME && input.trim().length === 0 ? 'Nueva carpeta' : name;
}

/**
 * Comprueba que una clave de R2 construida internamente no contenga
 * travesías de ruta. Defensa en profundidad: la clave siempre se compone en
 * backend a partir de UUIDs.
 */
export function isSafeStorageKey(key: string): boolean {
  if (key.length === 0 || key.length > 1024) return false;
  if (key.startsWith('/') || key.includes('//')) return false;
  if (new RegExp(CONTROL_CHARS.source, 'u').test(key)) return false;
  return !key.split('/').some((segment) => segment === '.' || segment === '..' || segment === '');
}

/**
 * Valor seguro para `Content-Disposition`.
 * Usa el formato RFC 5987 para conservar caracteres no ASCII sin permitir
 * inyección de cabeceras.
 */
export function contentDisposition(filename: string, type: 'inline' | 'attachment'): string {
  const safe = sanitizeFilename(filename);
  const ascii = safe.replace(NON_ASCII_PRINTABLE, '_').replace(/["\\]/g, '_');
  return `${type}; filename="${ascii}"; filename*=UTF-8''${encodeURIComponent(safe)}`;
}
